<script>
import Field from "@/components/input-v.vue";
import FormModal from "@/components/form-modal.vue";
import Modal from "@/components/g-modal.vue";
import { FieldTypeEnum } from "@/core/enums/field-types";
import { AnyAdmin } from "@/core/enums/user-types";
import getTextWidth from "@/helpers/getTextWidth";
import { isEmpty } from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";
import AttachModal from "../shared/vpl-attach-modal.vue";
import DetatchModal from "../shared/vpl-detach-modal.vue";
import VPLModal from "../shared/vpl-form-modal.vue";
import VplTable from "../shared/vpl-table.vue";
import { BusinessPlanStatus } from "../../businessplan.types";
import { TiposResponsaveisEnum } from "../../../responsaveis/responsaveis.types";

/**
 * @typedef RubricaVPL
 * @property {number | undefined} id
 * @property {string} rubrica
 * @property {string} tipo_rubrica
 * @property {number} rubrica_id
 * @property {object} competencias
 * @property {Object.<string, number>} competencias
 * @property {number} valor
 */

export default {
  components: {
    AttachModal,
    DetatchModal,
    Field,
    FormModal,
    Modal,
    VPLModal,
    VplTable
  },
  props: {
    disabled: Boolean,
    editable: Boolean,
    endDate: String,
    isDemandant: Boolean,
    hasAdminRole: Boolean,
    officialVersion: Boolean,
    startDate: String,
    status: Number,
    usefulLife: Number
  },
  data() {
    const colWidthMap = new Map();

    return {
      adminName: "Administrador",
      allocationMenuOpen: false,
      attachModal: {
        opened: false
      },
      colWidthMap,
      detachModal: {
        data: [],
        opened: false
      },
      editMode: "projetado",
      error: 0,
      expanded: [],
      exportModal: {
        filename: "",
        opened: false
      },
      formModal: {
        data: {},
        opened: false
      },
      projetados: [],
      realizados: [],
      rubricas: [],
      taxModal: {
        data: {},
        opened: false
      }
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    parsedEditable() {
      return (
        (this.editable || this.hasAdminRole) &&
        (this.officialVersion ||
          [
            BusinessPlanStatus.PREENCHIMENTO,
            BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO
          ].includes(this.status))
      );
    },
    actionBarButtons() {
      if (!this.parsedEditable) {
        return [];
      }

      const buttons = [
        {
          text: "Atualizar",
          icon: "mdi-refresh",
          action: () => {
            this.doLoad();
          }
        }
      ];

      if (this.hasAdminRole || AnyAdmin.includes(this.user.tipo_usuario)) {
        buttons.push({
          text: "Alterar taxa de retorno",
          icon: "mdi-percent-box-outline",
          action: async () => {
            const taxa_juros_anual = await this.doGetTax();
            this.taxModal.data = { taxa_juros_anual };
            this.taxModal.opened = true;
          }
        });
      }

      if (
        (!this.isInitialPhase || !this.officialVersion) &&
        (this.hasAdminRole ||
          this.isDemandant ||
          AnyAdmin.includes(this.user.tipo_usuario))
      ) {
        buttons.push({
          text: "Adicionar Retorno Financeiro",
          icon: "mdi-link-plus",
          action: async () => {
            this.attachModal.opened = true;
          }
        });

        if (this.filteredRubrica.length > 0) {
          buttons.push({
            text: "Remover Retorno Financeiro",
            icon: "mdi-link-off",
            action: async () => {
              this.detachModal.data = [];
              this.detachModal.opened = true;
            }
          });
        }
      }

      return buttons;
    },
    /**
     * Retorna a ultima data do projeto, que é a data final do projeto mais os anos de amortização
     * @returns {string} data no formato `YYYY-MM-DD`
     * @readonly
     */
    amortizationEndDate() {
      const date = moment(this.endDate, "YYYY-MM-DD");

      if (!date.isValid()) {
        return null;
      }

      if (!this.usefulLife) {
        return this.endDate;
      }

      return date.add(this.usefulLife, "years").format("YYYY-MM-DD");
    },
    businessPlanId() {
      return this.$route.params.id;
    },
    canUpdate() {
      const today = moment();
      const endDate = moment(this.endDate, "YYYY-MM-DD");
      return (
        !this.isInitialPhase || today.isSameOrBefore(endDate, "month")
      );
    },
    allocationButtonConfig() {
      if (this.isFillingPhase) {
        return {
          type: "direct",
          mode: "projetado",
          text: "Alocar valores Projetados"
        };
      }

      if (this.isExecutionPhase && !this.hasAdminRole) {
        return {
          type: "direct",
          mode: "realizado",
          text: "Alocar valores Realizados"
        };
      }

      return {
        type: "menu",
        text: "Alocar valores"
      };
    },
    showAllocationButton() {
      if (this.hasAdminRole) {
        return this.filteredRubrica.length > 0;
      }

      return this.canUpdate && this.filteredRubrica.length > 0;
    },
    cols() {
      const calcWidth = (length, defaultLength) =>
        Math.max(Math.round(length) + 32, defaultLength);
      const colWidthMap = this.colWidthMap;
      const endMonth = moment(this.endDate, "YYYY-MM-DD").format("YYYY-MM");
      const months = this.competencias.map(value => ({
        align: "end",
        divider: value === endMonth,
        text: moment(value, "YYYY-MM").format("MM/YYYY"),
        value,
        width: calcWidth(colWidthMap.get(value), 100)
      }));

      return [
        {
          text: this.isInitialPhase ? "Projetado" : "",
          value: "rubrica",
          width: calcWidth(colWidthMap.get("rubrica"), 300)
        },
        ...months,
        {
          text: "Total",
          value: "total"
        }
      ];
    },
    competencias() {
      const projectStart = this.startDate
        ? moment(this.startDate, "YYYY-MM")
        : moment();
      const projectEnd = this.amortizationEndDate
        ? moment(this.amortizationEndDate, "YYYY-MM")
        : moment();
      const size = projectEnd.diff(projectStart, "months") + 1;
      return Array.from({ length: size }, (_, index) => {
        return moment(projectStart)
          .add(index, "months")
          .format("YYYY-MM");
      });
    },
    filteredRubrica() {
      if (this.isAdmin) {
        return this.rubricas;
      }

      // Se for demandante, informa apenas Retorno Financeiro. Se não for demandante, pode qualquer tipo
      return this.rubricas.filter(
        ({ tipo_id }) => !this.isDemandant || tipo_id === 1
      );
    },
    fieldExport() {
      return {
        key: "filename",
        name: "Nome do arquivo",
        placeholder: "vpl-consolidado.xlsx",
        type: this.$fieldTypes.TEXT,
        rules: [{ rule: "required" }, { rule: "max", params: { size: 255 } }]
      };
    },
    isAdmin() {
      return AnyAdmin.includes(this.user.tipo_usuario);
    },
    isInitialPhase() {
      return ![
        BusinessPlanStatus.PREENCHIMENTO,
        BusinessPlanStatus.APROVACAO
      ].includes(this.status);
    },
    isFillingPhase() {
      return [
        BusinessPlanStatus.PREENCHIMENTO,
        BusinessPlanStatus.APROVACAO
      ].includes(this.status);
    },
    isExecutionPhase() {
      return [
        BusinessPlanStatus.ANDAMENTO,
        BusinessPlanStatus.PRE_AMORTIZACAO,
        BusinessPlanStatus.AMORTIZACAO
      ].includes(this.status);
    },
    projetadoEditable() {
      return (
        this.hasAdminRole ||
        [
          BusinessPlanStatus.PREENCHIMENTO,
          BusinessPlanStatus.APROVACAO
        ].includes(this.status) ||
        [
          BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO,
          BusinessPlanStatus.APROVACAO_NOVA_VERSAO
        ].includes(this.status)
      );
    },
    realizadoEditable() {
      return (
        this.hasAdminRole ||
        [
          BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO,
          BusinessPlanStatus.APROVACAO_NOVA_VERSAO
        ].includes(this.status) ||
        (this.isInitialPhase && this.officialVersion)
      );
    },
    rubricasVinculadasResource() {
      return this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/rubricas`
      );
    },
    taxFields() {
      return [
        {
          key: "taxa_juros_anual",
          name: "Taxa de juros",
          type: FieldTypeEnum.NUMBER,
          rules: [{ rule: "required" }]
        }
      ];
    },
    taxResource() {
      return this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/vpl/parametrizacao`
      );
    }
  },
  async created() {
    const requests = [
      this.doGetAdminName(),
      this.doGetRubricas(),
      this.doLoad()
    ];
    await Promise.all(requests);
  },
  methods: {
    /**
     * @method calcColumnsWidth
     * @param {RubricaVPL[]} projetados
     * @param {RubricaVPL[]} realizados
     * @returns {void}
     */
    calcColumnsWidth(projetados, realizados) {
      const toCurrencyValue = this.$options.filters.toCurrencyValue;
      const [firstMonth] = this.competencias;
      const widthMap = new Map();
      const allData = projetados.concat(...realizados);
      this.cols.forEach(({ value: key }) => {
        allData.forEach(({ competencias, valor, ...rest }) => {
          const k = isEmpty(competencias) ? firstMonth : key;
          const prevWidth = widthMap.get(k) || 0;
          const value = isEmpty(competencias)
            ? valor
            : key in rest
            ? rest[key]
            : key in competencias
            ? toCurrencyValue(competencias[key])
            : "";
          const valueWidth = Math.round(getTextWidth(value)) + 80;
          widthMap.set(k, Math.max(prevWidth, valueWidth));
        });
      });
      this.colWidthMap = widthMap;
    },
    async doLoad() {
      if (this.disabled) {
        this.projetados = [];
        this.realizados = [];
        this.calcColumnsWidth([], []);
        return;
      }

      try {
        const resource = this.apiResource(
          `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/vpl/rubrica`
        );
        const response = await resource.get();
        /** @type RubricaVPL[] */
        this.projetados = response.projetado;
        /** @type RubricaVPL[] */
        this.realizados = response.realizado;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.projetados = [];
        this.realizados = [];
      } finally {
        this.calcColumnsWidth(this.projetados, this.realizados);
      }
    },
    async doGetAdminName() {
      try {
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/nivel-hierarquicos/selecao`
        );
        const response = await get();
        const adminLevel = response.find(
          nivel => nivel.tipo_id === TiposResponsaveisEnum.ADMINISTRADOR
        );

        if (adminLevel && adminLevel.nome) {
          this.adminName = adminLevel.nome;
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async doGetRubricas() {
      try {
        const response = await this.rubricasVinculadasResource.get();
        this.rubricas = Array.isArray(response) ? response : [];
        return response;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.rubricas = [];
        return [];
      }
    },
    async doGetTax() {
      try {
        const response = await this.taxResource.get();
        const value = response.taxa_juros_anual || "";
        return value;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        return "";
      }
    },
    /**
     * Nome de arquivo sempre novo: BP + data/hora da exportação, para nunca
     * colidir com a validação de nome único do backend.
     * @returns {string}
     */
    defaultExportFilename() {
      const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
      return `vpl-consolidado-${this.businessPlanId}-${timestamp}.xlsx`;
    },
    async doExport() {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/exportacao`
        );
        const filename = this.exportModal.filename || this.defaultExportFilename();
        await resource.save({
          tipo: "vpl-consolidado",
          arquivo_nome: this.sanitizeFilename(filename),
          filtros: {
            businessPlanId: this.businessPlanId
          }
        });

        this.$notify({
          group: "geral",
          duration: 10000,
          type: "info",
          title: `Exportação em processamento!`,
          text: `Quando concluída, você vai ser notificada pelo sistema e o arquivo vai estar disponível na aba Memória de cálculo e Evidências do Business Plan.`
        });

        this.exportModal.opened = false;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async doSaveRubrica(data, close) {
      try {
        if (!Array.isArray(data)) {
          return;
        }

        const url = `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/vpl/rubrica/${this.editMode}`;
        const resource = this.apiResource(url);
        await resource.save({ rubricas: data });
        this.$emit("reload");
        this.doLoad();

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Cadastro concluído com sucesso.",
          text: `Foram atualizadas ${data.length} valores deste Business Plan.`
        });

        if (close) {
          close();
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async doSaveTax(data, close) {
      try {
        const { taxa_juros_anual } = data;
        await this.taxResource.save({
          taxa_juros_anual: taxa_juros_anual
        });

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Taxa de juros alterada com sucesso.",
          text: "Taxa de juros foi alterada para este BP."
        });

        if (close) {
          close();
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    onDetachModal(row) {
      this.detachModal.data = [row.rubrica_id];
      this.detachModal.opened = true;
    },
    async onEditModal(mode = "projetado", row) {
      await this.doGetRubricas();
      this.editMode = mode;
      this.$refs.vplModal.open(row.rubrica_id);
    },
    async openAllocationModal(mode) {
      await this.doGetRubricas();
      this.editMode = mode;
      this.$refs.vplModal.open();
    },
    sanitizeFilename(filename = "") {
      let safeName = filename
        .replace(/[\\\/:*?"<>|]/g, "") // Remove caracteres especiais
        .replace(/\s+/g, "-") // Remove espaços
        .replace(/[\.\s]+$/, "") // Remove pontos além do ponto da extensão
        .normalize("NFD") // Normaliza para separar o código dos acentos do código das letras
        .replace(/[\u0300-\u036f]/g, "") // Remove o código de todos os acentos
        .toLowerCase();

      if (!/\.xlsx$/i.test(safeName)) {
        safeName += ".xlsx";
      }

      return safeName;
    },
    /**
     * @method validate
     * @param {'approve' | 'save'} mode
     * @returns {boolean}
     */
    validate(mode = "save") {
      const rubricas = this.projetados.filter(({ rubrica_id }) => !!rubrica_id);

      if (mode === "approve" && !rubricas.length) {
        this.$emit("error", 1);
        return false;
      }

      this.$emit("error", 0);
      return true;
    }
  },
  watch: {
    disabled(value) {
      if (!value && (!this.projetados.length || !this.realizados.length)) {
        this.doLoad();
      }
    },
    projetados() {
      if (!this.disabled && this.error > 0) {
        this.validate("approve");
      }
    }
  }
};
</script>

<template>
  <v-card :color="tableColor" elevation="0">
    <v-card-text>
      <div class="d-flex flex-wrap">
        <div
          v-for="(button, idx) in actionBarButtons"
          class="table-v-action-button mr-2"
          :key="'action-bar-' + idx"
          @click="button.action"
        >
          <v-icon>{{ button.icon }}</v-icon>
          {{ button.text }}
        </div>

        <div
          v-if="
            showAllocationButton &&
            allocationButtonConfig.type === 'direct'
          "
          class="table-v-action-button mr-2"
          @click="openAllocationModal(allocationButtonConfig.mode)"
        >
          <v-icon>mdi-currency-usd</v-icon>
          {{ allocationButtonConfig.text }}
        </div>

        <v-menu
          v-else-if="
            showAllocationButton &&
            allocationButtonConfig.type === 'menu'
          "
          v-model="allocationMenuOpen"
          bottom
          offset-y
        >
          <template v-slot:activator="{ attrs, on }">
            <div
              class="table-v-action-button mr-2"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-currency-usd</v-icon>
              {{ allocationButtonConfig.text }}
            </div>
          </template>

          <v-list>
            <v-list-item @click="openAllocationModal('projetado')">
              Valores Projetados
            </v-list-item>
            <v-list-item @click="openAllocationModal('realizado')">
              Valores Realizados
            </v-list-item>
          </v-list>
        </v-menu>

        <v-spacer />

        <div
          class="table-v-action-button"
          @click="
            exportModal.filename = defaultExportFilename();
            exportModal.opened = true;
          "
        >
          <v-icon>mdi-file-export-outline</v-icon>
          Exportação
        </div>
      </div>

      <div class="d-flex align-center items-center mt-2">
        <v-icon left class="mr-2" style="color: #fbb900">
          mdi-alert-circle
        </v-icon>
        <span v-if="canUpdate" style="color: #ffffff">
          Atenção: O cadastro de rubricas People (Capex) é realizado por
          {{ adminName }}.
        </span>
        <span v-else style="color: #ffffff; font-size: 16px">
          Atenção: Não é possível cadastro de valores realizados antes do começo
          da amortização do projeto.
        </span>
      </div>
    </v-card-text>

    <VplTable
      :cols="cols"
      :projetadoRows="projetados"
      :projetadoEditable="projetadoEditable"
      :realizadoRows="realizados"
      :realizadoEditable="realizadoEditable"
      :showRealizado="isInitialPhase"
      :competencias="competencias"
      :disabledDetatch="false"
      @click:edit-projetado="onEditModal('projetado', $event)"
      @click:edit-realizado="onEditModal('realizado', $event)"
      @click:detatch-projetado="onDetachModal"
    />

    <AttachModal
      :businessPlanId="businessPlanId"
      :isDemandant="isDemandant"
      :hasAdminRole="hasAdminRole"
      :opened.sync="attachModal.opened"
      :rubricas="rubricas"
      @reload="
        $emit('reload');
        doLoad();
        doGetRubricas();
      "
    />

    <DetatchModal
      :businessPlanId="businessPlanId"
      :opened.sync="detachModal.opened"
      :rubricas="rubricas"
      :value="detachModal.data"
      @reload="
        $emit('reload');
        doLoad();
        doGetRubricas();
      "
    />

    <FormModal
      :cols="taxFields"
      :opened.sync="taxModal.opened"
      title="Alterar taxa de retorno"
      titleIcon="mdi-percent-box-outline"
      :value.sync="taxModal.data"
      @save="doSaveTax"
    />

    <VPLModal
      ref="vplModal"
      :amortizationEndDate="amortizationEndDate"
      :competencias="competencias"
      :editMode="editMode"
      :endDate="endDate"
      :hasAdminRole="hasAdminRole"
      :projetados="projetados"
      :realizados="realizados"
      :rubricas="filteredRubrica"
      :startDate="startDate"
      :status="status"
      @save="doSaveRubrica"
    />

    <Modal
      :opened.sync="exportModal.opened"
      title="Exportar VPL e Outras Despesas"
      width="600px"
    >
      <p>O processamento do arquivo é feito de forma assíncrona pelo Teros.</p>
      <p>
        Informe o nome do arquivo e aguarde a notificação de que o mesmo já está
        disponível para download.
      </p>

      <Field v-model="exportModal.filename" :label="fieldExport" />

      <template #buttons>
        <v-spacer />

        <v-btn
          class="px-5 ml-3"
          color="secondary"
          depressed
          @click="exportModal.opened = false"
        >
          Cancelar
        </v-btn>

        <v-btn
          class="px-5 ml-3"
          color="primary"
          dark
          depressed
          @click="doExport"
        >
          Exportar
        </v-btn>
      </template>
    </Modal>
  </v-card>
</template>

<style lang="scss" scoped>
.v-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;

  .v-card__text {
    flex-shrink: 0;
  }
}

.projetado::v-deep.v-data-table:not(.expanded-table) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  flex-grow: unset;
}

.realizado::v-deep.v-data-table:not(.expanded-table) {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
